/**
 * ConflictDialog.jsx - r50 save guards of the record editor
 * (EditOpportunityModal), which edits opportunities and projects.
 *
 * Sharif (10/2/26): two people working on one opportunity must never end up
 * with the same quote number, or with one save quietly undoing the other's.
 * The server refuses both cases with a 409 and nothing is written:
 *   quote_taken    the quote number is already on another record (main or
 *                  GLOBAL KSA, one series). Duplicates are legitimate on
 *                  purpose - one quote can have a Paint and a Sealer line -
 *                  so the user may keep it.
 *   edit_conflict  a field this save changes was changed by someone else
 *                  since the editor opened.
 * runGuardedSave sends the save and answers each refusal with the user
 * (useSaveGuard + SaveGuardDialog). The dialogs never close the editor: what
 * was typed stays in it whatever is picked.
 *
 * Also here: the "someone just changed this record" banner (live events,
 * src/live) and the next-quote prefill of a new record.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { createPortal } from 'react-dom';
import { matchRecord, recordPart, scopeKindOf, useLiveEvent } from '../../live/useLive';
import { camelOf, conflictLine, conflictsOf, fetchNextQuote, fieldLabel, takeOfferedQuote } from './editOpportunityForm';

/**
 * Send a save and answer the server's refusals with the user.
 *   quote_taken   -> 'next' (onQuote(next), send again with that number),
 *                    'keep' (send again with allowDuplicateQuote) or 'cancel'
 *   edit_conflict -> 'mine' (send again with force) or 'theirs'
 *                    (onTheirs(body); nothing is sent)
 * `send(opts)` -> Promise<{ ok, status, body }>, opts = { quoteLetter?,
 * allowDuplicateQuote?, force? } (each kept for the sends after it).
 * `ask(kind, body)` -> Promise<choice>, kind 'quote' | 'conflict'.
 * Resolves to the server's last answer, or null when the user stopped at a dialog.
 */
export async function runGuardedSave({ send, ask, onQuote, onTheirs }) {
  let opts = {};
  for (;;) {
    // a send that throws (a dropped connection) is a failed save, not a stuck button
    const these = opts;
    const res = await Promise.resolve()
      .then(() => send(these))
      .catch(() => ({ ok: false, status: 0, body: null }));
    const code = res && res.status === 409 && res.body ? res.body.code : null;
    if (code === 'quote_taken') {
      const choice = await ask('quote', res.body);
      const next = res.body.next != null ? String(res.body.next).trim() : '';
      if (choice === 'next' && next) {
        if (onQuote) onQuote(next);
        opts = { ...opts, quoteLetter: next };
        continue;
      }
      if (choice === 'keep') {
        opts = { ...opts, allowDuplicateQuote: true };
        continue;
      }
      return null;
    }
    if (code === 'edit_conflict') {
      const choice = await ask('conflict', res.body);
      if (choice === 'mine') {
        opts = { ...opts, force: true };
        continue;
      }
      if (choice === 'theirs' && onTheirs) onTheirs(res.body);
      return null;
    }
    return res;
  }
}

/** The dialog state for runGuardedSave: `ask` opens one and waits for the answer. */
export function useSaveGuard() {
  const [asking, setAsking] = useState(null);
  const resolver = useRef(null);
  const ask = useCallback(
    (kind, body) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setAsking({ kind, body: body || {} });
      }),
    []
  );
  const answer = useCallback((choice) => {
    const r = resolver.current;
    resolver.current = null;
    setAsking(null);
    if (r) r(choice);
  }, []);
  // an editor closed with a dialog open answers it, so no save is left waiting
  useEffect(() => {
    const pending = resolver;
    return () => {
      if (pending.current) pending.current('cancel');
    };
  }, []);
  return { ask, asking, answer };
}

const BTN = { padding: '9px 16px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'Montserrat' };
const BTN_MAIN = { ...BTN, background: '#1B5E20', color: '#fff', border: 'none' };
const BTN_ALT = { ...BTN, background: '#fff', color: '#1B5E20', border: '1px solid #C8E6C9' };
const BTN_QUIET = { ...BTN, background: '#fff', color: '#555', border: '1px solid #E0E0E0', fontWeight: 600 };

/* Its own overlay, not ui/Modal: Escape in a Modal closes every open Modal,
   and here that would close the editor and lose what was typed. Escape (or
   Cancel) goes back to the editor; the backdrop does nothing. */
function GuardShell({ title, onEscape, children }) {
  const drag = useDraggableDialog(); // moved by its title (10/9/26)
  const escRef = useRef(onEscape);
  useEffect(() => {
    escRef.current = onEscape;
  });
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation(); // capture phase: the editor's Modal never hears it
      e.preventDefault();
      escRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="go-pop-in"
        ref={drag.ref}
        onPointerDown={drag.onPointerDown}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 'min(540px, 100%)',
          maxHeight: '86vh',
          overflowY: 'auto',
          boxShadow: '0 18px 48px rgba(0,0,0,0.25)',
          padding: '22px 26px',
          fontFamily: "'Montserrat', sans-serif",
          ...drag.style,
        }}
      >
        <div data-dialog-handle="" style={{ fontSize: 15, fontWeight: 800, color: '#0D0D0D', marginBottom: 8 }}>
          {title}
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

/** 409 edit_conflict: each field someone else changed, then Keep mine / Use theirs. */
export function EditConflictDialog({ body, labels, onAnswer }) {
  const conflicts = conflictsOf(body);
  return (
    <GuardShell title="Someone else changed this record" onEscape={() => onAnswer('cancel')}>
      <div style={{ fontSize: 12, color: '#555', lineHeight: 1.5, marginBottom: 12 }}>
        {(body && body.error) || 'Someone else changed this record while you were editing it.'} Nothing was saved.
      </div>
      <ul style={{ margin: '0 0 14px', padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
        {conflicts.map((c) => (
          <li
            key={c.key}
            style={{
              background: '#FFF8E1',
              border: '1px solid #FDE68A',
              color: '#92400E',
              borderRadius: 6,
              padding: '7px 10px',
              fontSize: 11.5,
              fontWeight: 600,
              lineHeight: 1.45,
            }}
          >
            {conflictLine(c, body && body.changed_by, labels)}
          </li>
        ))}
      </ul>
      <div style={{ fontSize: 11, color: '#777', lineHeight: 1.5, marginBottom: 16 }}>
        Keep mine saves your values over theirs. Use theirs puts their values in the form and saves nothing yet; everything else you typed
        stays.
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, flexWrap: 'wrap' }}>
        <button type="button" style={BTN_ALT} onClick={() => onAnswer('theirs')}>
          Use theirs
        </button>
        <button type="button" style={BTN_MAIN} onClick={() => onAnswer('mine')} autoFocus>
          Keep mine
        </button>
      </div>
    </GuardShell>
  );
}

/** 409 quote_taken: use the next free number, keep this one (another line of the same quote), or go back. */
export function QuoteTakenDialog({ body, onAnswer }) {
  const quote = String((body && body.quote) || '').trim();
  // '' when the server has no numeric quote to go on: no "Use" button then
  const next = body && body.next != null ? String(body.next).trim() : '';
  const t = (body && body.taken_by) || {};
  // The server leaves out a GLOBAL KSA record's names for those who cannot
  // open KSA (customer and name come as null): its own sentence says what it
  // can, else the record is called what it is.
  const named = [t.customer, t.name]
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(' · ');
  const said = String((body && body.error) || '').trim();
  return (
    <GuardShell title={`Quote ${quote} is already used`} onEscape={() => onAnswer('cancel')}>
      <div style={{ fontSize: 12.5, color: '#333', lineHeight: 1.5, marginBottom: 16 }}>
        {named ? (
          <>
            Quote {quote} was just used for <b>{named}</b>
            {t.ksa ? ' (a GLOBAL KSA record)' : ''}.
          </>
        ) : said ? (
          <>{/[.!?]$/.test(said) ? said : `${said}.`}</>
        ) : (
          <>
            Quote {quote} was just used for <b>{t.ksa === false ? 'another record' : 'a GLOBAL KSA record'}</b>.
          </>
        )}{' '}
        Nothing was saved.
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, flexWrap: 'wrap' }}>
        <button type="button" style={BTN_QUIET} onClick={() => onAnswer('cancel')}>
          Cancel
        </button>
        <button type="button" style={BTN_ALT} onClick={() => onAnswer('keep')}>{`Keep ${quote} - another line of the same quote`}</button>
        {next && <button type="button" style={BTN_MAIN} onClick={() => onAnswer('next')} autoFocus>{`Use ${next}`}</button>}
      </div>
    </GuardShell>
  );
}

/** Whichever dialog useSaveGuard is asking, or nothing. */
export default function SaveGuardDialog({ asking, answer, labels }) {
  if (!asking) return null;
  return asking.kind === 'quote' ? (
    <QuoteTakenDialog body={asking.body} onAnswer={answer} />
  ) : (
    <EditConflictDialog body={asking.body} labels={labels} onAnswer={answer} />
  );
}

/**
 * The open record's live changes by other people (src/live): the record's own
 * events (matchRecord - kind 'projects' with no sub-resource, or on the KSA
 * tab kind 'ksa' sub 'projects'), naming the fields this form has. Comments,
 * files and tasks on the record carry no field changes and are ignored.
 * On the KSA tab every event of the record is sub 'projects', its purchases,
 * comments and files too - and a purchase's own fields (status, amount,
 * currency) would read as the record's - so there only the event's path
 * tells (recordPart): one under the record's path is never this banner's
 * business, and an event without a path that names no field is not either.
 * A record deleted elsewhere says so (on the KSA tab, when the path is the
 * record's own). `base` is useApiBase(). Returns [note | null, dismiss].
 */
export function useRecordChangeNote(recordId, base, labels) {
  const [note, setNote] = useState(null);
  const inScope = !!scopeKindOf(base);
  const match = useMemo(() => matchRecord(base, recordId, null), [base, recordId]);
  const onEvent = useCallback(
    (evt) => {
      const part = inScope ? recordPart(evt, base, recordId) : null;
      if (part) return; // a purchase, note or file of the record
      const actor = evt.actor || evt.actorEmail || 'Someone';
      if (evt.method === 'DELETE' && (!inScope || part === '')) {
        setNote({ actor, deleted: true, fields: [] });
        return;
      }
      const keys = (Array.isArray(evt.fields) ? evt.fields : []).map(camelOf).filter((k) => labels && labels[k]);
      if (!keys.length) return;
      setNote((prev) => {
        const before = prev && !prev.deleted && prev.actor === actor ? prev.fields : [];
        return { actor, deleted: false, fields: [...new Set([...before, ...keys])] };
      });
    },
    [inScope, base, recordId, labels]
  );
  useLiveEvent(match, onEvent);
  const dismiss = useCallback(() => setNote(null), []);
  return [note, dismiss];
}

/** The banner for useRecordChangeNote's note. */
export function LiveChangeBanner({ note, labels, onDismiss, style }) {
  if (!note) return null;
  const text = note.deleted
    ? `${note.actor} just deleted this record.`
    : `${note.actor} just changed this record (${note.fields.map((k) => fieldLabel(k, labels)).join(', ')}). Saving only changes the fields you edited.`;
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        background: '#FFF8E1',
        border: '1px solid #FDE68A',
        color: '#92400E',
        borderRadius: 6,
        padding: '7px 10px',
        fontSize: 11.5,
        fontWeight: 600,
        lineHeight: 1.45,
        ...style,
      }}
    >
      <span style={{ flex: 1 }}>{text}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ background: 'none', border: 'none', fontSize: 15, lineHeight: 1, cursor: 'pointer', color: '#92400E', padding: 0 }}
      >
        ×
      </button>
    </div>
  );
}

/**
 * A new record's Quote field: filled with the server's next free number
 * (fetchNextQuote), and again whenever the loaded records change (someone
 * else's new record arrives live) while it still shows our own offer. A
 * number the user typed is never overwritten. `setQuote(next, current)` must
 * only replace `current`. Returns the ref of the last offer, so a number the
 * app puts there itself ("Next #", "Use 5392") counts as an offer too.
 */
export function useQuotePrefill({ enabled, base, allProjects, quote, setQuote }) {
  const offered = useRef('');
  const latest = useRef(quote);
  latest.current = quote;
  const setRef = useRef(setQuote);
  setRef.current = setQuote;
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    fetchNextQuote(base, allProjects).then((n) => {
      if (!live) return;
      const cur = latest.current;
      const v = takeOfferedQuote(cur, offered.current, n);
      if (v == null) return;
      offered.current = v;
      setRef.current(v, cur);
    });
    return () => {
      live = false;
    };
  }, [enabled, base, allProjects]);
  return offered;
}
